import React, { useContext, useEffect } from 'react';

import ConsentCheckbox from '@/components/order/order-summary/ConsentCheckbox';
import { PartnerKey } from '@/hooks/portal-partner-brand';
import { PreferencesContext } from '@montugroup/circuit-shared/context/Preferences';
import useUserProfileData from '@montugroup/circuit-shared/hooks/user/useGetUserProfile';
import { Box, CircularProgress } from '@montugroup/design-system';

interface UmedsMarketingConsentProps {
  size?: 'small' | 'medium';
}

export const UMEDS_MARKETING_CONSENT_TEXT =
  'Keep me up to date with shipping promotions and the latest news from uMeds';

export const UMEDS_ACCESS_PRESCRIPTION_CONSENT_TEXT =
  'By proceeding, I agree that uMeds can access my personal and prescription information using the encrypted payload from eRx (electronic prescription) for the purpose of ordering medication.';

export default function UmedsMarketingConsent({ size }: UmedsMarketingConsentProps) {
  const { data: userData, isLoading } = useUserProfileData();
  const isOptedIn = userData?.communications?.umeds_marketing_opted_in;

  // Don't lump in users with no preference set as opted in
  const isExplicitlyOptedIn = userData?.communications?.umeds_marketing_opted_in === true;

  const isUmeds = PartnerKey.UMEDS === 'uMeds';

  const { umedsMarketingConsent, setUmedsMarketingConsent } = useContext(PreferencesContext);

  // Initialize the consent state based on the user's preference
  useEffect(() => {
    if (isLoading) {
      return;
    }

    // A user with no preference set will have `undefined` here, so we opt them in by default
    setUmedsMarketingConsent(isOptedIn !== false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUmedsMarketingConsent(event.target.checked);
  };

  if (!isUmeds || isExplicitlyOptedIn) {
    return null;
  }

  if (isLoading) {
    return <CircularProgress />;
  }

  return (
    <Box data-testid="consent-checkbox">
      <ConsentCheckbox
        trackingId="UmedsMarketingConsent"
        text={UMEDS_MARKETING_CONSENT_TEXT}
        size={size}
        checked={!!umedsMarketingConsent}
        onChange={handleChange}
      />
    </Box>
  );
}
